import { AnalysisStatusI } from '@/interfaces/AnalysisStatusI';
import { LabelsetType } from '@/interfaces/types/Labelset';
import {
  LabelQualityGoal,
  QualityGoalType,
} from '@/interfaces/types/QualityGoal';
import { JSONSchema7 } from 'json-schema';

// #region axios response body typing
export type __Labelset_Type__ = {
  dataset_id: string;
  labelset_id: string;
  name: string;
  description: string;
  label_schema: JSONSchema7;
  parsing_rule: __Parsing_Rule_Type__;
  is_modified: boolean;
  analysis_status: AnalysisStatusI;
  quality_goal_id?: string;
  report_timeline_list?: {
    quality_goal_id: string;
    previous_node: string;
    next_node: string;
  }[];
  updated_at: Date;
  created_at: Date;
};

type propertiesType = string | Array<propertiesType>;

export type __Parsing_Rule_Type__ = {
  label_type: string;
  target_path: string;
  source_file: string;
  properties: Array<propertiesType>;
  seperator: Array<string>;
  id_prop: string;
};

export type __Quality_Goal_Type__ = {
  quality_goal_id?: string;
  workspace_id?: string;
  labelset_list?: LabelsetType[];
  name: string;
  description: string;
  quality_goal: {
    label_quality_goal: {
      [name: string]: __Label_Quality_Goal__;
    };
    meta_quality_goal: __Meta_Quality_Goal__;
  };
};

export type __Meta_Quality_Goal__ = {
  /** 구문 정확성 오류율 */
  schema_error_rate: number;
  /** 목표 데이터 수량 */
  quantity_goal: number;
};

export type __Label_Quality_Goal__ = {
  label_name: string;
  description: string;
  type: '' | 'string' | 'number' | 'object' | 'array';
  method:
    | ''
    | 'dist'
    | 'classQuantity'
    | 'minAvgMax'
    | 'arrLenMinAvgMax'
    | 'arrElCnt';
  target?: string;
  classes?: { [class_name: string]: string };
  reportOption?: { showStandard: boolean; showEtc: boolean };
} & (
  | {
      method: '';
    }
  | {
      method: 'dist';
      distribution_rate?: { [class_name: string]: number };
    }
  | {
      method: 'classQuantity';
      class_quantity?: { [class_name: string]: number };
    }
  | {
      method: 'minAvgMax' | 'arrLenMinAvgMax';
      average?: number;
      min_value?: number;
      max_value?: number;
    }
  | {
      method: 'arrElCnt';
      element_count?: number;
    }
);
// #endregion

// #region converting between camelCase <=> snake_case

// 너무 비효율적이지 않나 고민 필요 (커뮤니케이션 실패의 부채)
// 이름 통일시키겠다고 이정도까지의 정성을??
// TBD : 백엔드 - 프론트엔드 데이터 네이밍 컨벤션 통일

export function snakeToCamelQualityGoalType(
  quality_goal_type: __Quality_Goal_Type__,
): QualityGoalType {
  const labelQualityGoal: { [name: string]: LabelQualityGoal } = {};
  const label_quality_goal = quality_goal_type.quality_goal.label_quality_goal;

  if (label_quality_goal)
    Object.keys(label_quality_goal).forEach((key) => {
      const snake = label_quality_goal[key];
      const camel: LabelQualityGoal = {
        description: snake.description,
        type: snake.type,
        labelName: snake.label_name,
        method: snake.method,
        target: snake.target,
        classes: snake.classes,
        reportOption: snake.reportOption,
      };

      if (camel.method === 'dist' && snake.method === 'dist') {
        camel.distributionRate = snake.distribution_rate;
      } else if (
        camel.method === 'classQuantity' &&
        snake.method === 'classQuantity'
      ) {
        camel.classQuantity = snake.class_quantity;
      } else if (
        (camel.method === 'minAvgMax' && snake.method === 'minAvgMax') ||
        (camel.method === 'arrLenMinAvgMax' &&
          snake.method === 'arrLenMinAvgMax')
      ) {
        camel.average = snake.average;
        camel.maxValue = snake.max_value;
        camel.minValue = snake.min_value;
      } else if (camel.method === 'arrElCnt' && snake.method === 'arrElCnt') {
        camel.elementCount = snake.element_count;
      }

      labelQualityGoal[key] = camel;
    });

  return {
    name: quality_goal_type.name,
    description: quality_goal_type.description,
    qualityGoal: {
      labelQualityGoal: labelQualityGoal,
      metaQualityGoal: {
        schemaErrorRate:
          quality_goal_type.quality_goal.meta_quality_goal.schema_error_rate,
        quantityGoal:
          quality_goal_type.quality_goal.meta_quality_goal.quantity_goal,
      },
    },
    workspaceId: quality_goal_type.workspace_id,
    labelsetList: quality_goal_type.labelset_list,
    qualityGoalId: quality_goal_type.quality_goal_id,
  };
}

export function camelToSnakeQualityGoalType(
  qualityGoalType: QualityGoalType,
): __Quality_Goal_Type__ {
  const label_quality_goal: { [name: string]: __Label_Quality_Goal__ } = {};
  const labelQualityGoal = qualityGoalType.qualityGoal.labelQualityGoal;

  if (labelQualityGoal)
    Object.keys(labelQualityGoal).forEach((key) => {
      const camel = labelQualityGoal[key];
      const snake: __Label_Quality_Goal__ = {
        description: camel.description,
        type: camel.type,
        label_name: camel.labelName,
        method: camel.method,
        target: camel.target,
        classes: camel.classes,
        reportOption: camel.reportOption,
      };

      if (camel.method === 'dist' && snake.method === 'dist') {
        snake.distribution_rate = camel.distributionRate;
      } else if (
        camel.method === 'classQuantity' &&
        snake.method === 'classQuantity'
      ) {
        snake.class_quantity = camel.classQuantity;
      } else if (
        (camel.method === 'minAvgMax' && snake.method === 'minAvgMax') ||
        (camel.method === 'arrLenMinAvgMax' &&
          snake.method === 'arrLenMinAvgMax')
      ) {
        snake.average = camel.average;
        snake.max_value = camel.maxValue;
        snake.min_value = camel.minValue;
      } else if (camel.method === 'arrElCnt' && snake.method === 'arrElCnt') {
        snake.element_count = camel.elementCount;
      }

      label_quality_goal[key] = snake;
    });

  return {
    name: qualityGoalType.name,
    description: qualityGoalType.description,
    quality_goal: {
      label_quality_goal: label_quality_goal,
      meta_quality_goal: {
        schema_error_rate:
          qualityGoalType.qualityGoal.metaQualityGoal.schemaErrorRate,
        quantity_goal: qualityGoalType.qualityGoal.metaQualityGoal.quantityGoal,
      },
    },
    workspace_id: qualityGoalType.workspaceId,
    labelset_list: qualityGoalType.labelsetList,
    quality_goal_id: qualityGoalType.qualityGoalId,
  };
}

export function snakeToCamelLabelsetType(
  labelset_type: __Labelset_Type__,
): LabelsetType {
  return {
    datasetId: labelset_type.dataset_id,
    labelsetId: labelset_type.labelset_id,
    name: labelset_type.name,
    description: labelset_type.description,
    labelSchema: labelset_type.label_schema,
    parsingRule: {
      labelType: labelset_type.parsing_rule.label_type,
      targetPath: labelset_type.parsing_rule.target_path,
      sourceFile: labelset_type.parsing_rule.source_file,
      properties: labelset_type.parsing_rule.properties,
      seperator: labelset_type.parsing_rule.seperator,
      idProp: labelset_type.parsing_rule.id_prop,
    },
    isModified: labelset_type.is_modified,
    analysisStatus: labelset_type.analysis_status,
    qualityGoalId: labelset_type.quality_goal_id,
    reportTimelineList: labelset_type.report_timeline_list
      ? labelset_type.report_timeline_list.map((l) => {
          return {
            qualityGoalId: l.quality_goal_id,
            previousNode: l.previous_node,
            nextNode: l.next_node,
          };
        })
      : undefined,
    createdAt: labelset_type.created_at,
    updatedAt: labelset_type.updated_at,
  };
}

export function camelToSnakeLabelsetType(
  labelsetType: LabelsetType,
): __Labelset_Type__ {
  return {
    dataset_id: labelsetType.datasetId,
    labelset_id: labelsetType.labelsetId,
    name: labelsetType.name,
    description: labelsetType.description,
    label_schema: labelsetType.labelSchema,
    parsing_rule: {
      label_type: labelsetType.parsingRule.labelType,
      target_path: labelsetType.parsingRule.targetPath,
      source_file: labelsetType.parsingRule.sourceFile,
      properties: labelsetType.parsingRule.properties,
      seperator: labelsetType.parsingRule.seperator,
      id_prop: labelsetType.parsingRule.idProp,
    },
    is_modified: labelsetType.isModified,
    analysis_status: labelsetType.analysisStatus,
    quality_goal_id: labelsetType.qualityGoalId,
    report_timeline_list: labelsetType.reportTimelineList
      ? labelsetType.reportTimelineList.map((l) => {
          return {
            quality_goal_id: l.qualityGoalId,
            previous_node: l.previousNode,
            next_node: l.nextNode,
          };
        })
      : undefined,
    created_at: labelsetType.createdAt,
    updated_at: labelsetType.updatedAt,
  };
}
